<template>
    <div class="filter-suggestions-modal">
        <div class="modal-header">
            <div class="header-left">
                <div class="header-icon">
                    <i class="material-icons">tune</i>
                </div>
                <div class="header-text">
                    <h2 class="header-title">Filtre Önerileri</h2>
                    <p class="header-subtitle">AI asistan ile özel filtre oluşturun veya hazır şablonları kullanın</p>
                </div>
            </div>
        </div>

        <div class="ai-section">
            <div class="ai-header">
                <i class="material-icons">auto_awesome</i>
                <span>AI Asistan</span>
            </div>

            <div class="ai-input-wrapper">
                <S2Textarea
                    v-model="aiPrompt"
                    :rows="3"
                    placeholder="Örn: Son 11 gün içinde WhatsApp kanalından gelen VIP müşteriler"
                    :disabled="isGenerating"
                    ref="aiTextarea"
                />
                <SupsisButton
                    primary
                    :disabled="!aiPrompt || isGenerating"
                    @click="generateAiFilter"
                    class="ai-generate-btn"
                >
                    <i class="material-icons" v-if="!isGenerating">auto_fix_high</i>
                    <a-spin v-else :spinning="true" size="small" />
                    {{ isGenerating ? 'Oluşturuluyor...' : 'Filtre Oluştur' }}
                </SupsisButton>
            </div>

            <div v-if="isGenerating" class="progress-indicator">
                <a-spin :spinning="true" size="small" />
                <span>Filtreniz oluşturuluyor...</span>
            </div>
            <div v-if="aiFilterResult" class="ai-result-container">
                <div class="result-header">
                    <h3 class="result-title">{{ aiFilterResult.title }}</h3>
                    <p class="result-description">{{ aiFilterResult.description }}</p>
                </div>

                <div class="filters-container">
                    <div class="filters-count">
                        <i class="material-icons">check_circle</i>
                        <span>{{ aiFilterResult.filters.length }} Filtre Kuralı</span>
                    </div>

                    <div class="filters-grid">
                        <div
                            v-for="(filter, index) in aiFilterResult.filters"
                            :key="filter.id || index"
                            class="filter-item"
                        >
                            <div class="filter-number">{{ index + 1 }}</div>
                            <div class="filter-body">
                                <div class="filter-label">{{ getFilterFieldLabel(filter.field) }}</div>
                                <div class="filter-value-row">
                                    <span v-if="filter.operator" class="filter-operator">{{ getOperatorLabel(filter.operator) }}</span>
                                    <span class="filter-value">{{ formatFilterValueDisplay(filter) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="debugMode" class="debug-panel">
                        <div class="debug-title">
                            <i class="material-icons">code</i>
                            Filters JSON
                        </div>
                        <pre class="debug-code">{{ JSON.stringify(aiFilterResult.filters, null, 2) }}</pre>
                    </div>
                </div>

                <div class="result-actions">
                    <SupsisButton primary block @click="createFilterFromAi">
                        <i class="material-icons">add</i>
                        Filtreyi Oluştur
                    </SupsisButton>
                    <SupsisButton ghost @click="aiFilterResult = null">
                        İptal
                    </SupsisButton>
                </div>
            </div>

            <div class="example-prompts">
                <h4>Örnek Promptlar</h4>
                <div class="prompt-slider-container">
                    <div class="prompt-slider" ref="slider">
                        <div class="prompt-slide" v-for="(prompt, index) in examplePrompts" :key="index">
                            <div class="prompt-card" @click="selectExamplePrompt(prompt)">
                                <i class="material-icons">lightbulb</i>
                                <p>{{ prompt }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="slider-controls">
                        <a-button class="control-btn prev" @click="slidePrev">
                            <i class="material-icons">chevron_left</i>
                        </a-button>
                        <a-button class="control-btn next" @click="slideNext">
                            <i class="material-icons">chevron_right</i>
                        </a-button>
                    </div>
                </div>
            </div>
        </div>


        <div class="templates-section">
            <div class="section-header">
                <h3 class="section-title">Hazır Şablonlar</h3>
                <div class="search-box">
                    <i class="material-icons">search</i>
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Şablonlarda ara..."
                        class="search-input"
                    />
                </div>
            </div>

            <div class="filter-groups">
                <div
                    v-for="group in filterGroups"
                    :key="group.id"
                    class="group-badge"
                    :class="{ active: selectedGroup === group.id }"
                    @click="selectGroup(group.id)"
                >
                    <i class="material-icons">{{ group.icon }}</i>
                    <span>{{ group.name }}</span>
                    <span class="group-count">{{ getGroupTemplateCount(group.id) }}</span>
                </div>
            </div>

            <div class="templates-slider-container">
                <div class="templates-grid" ref="templatesSlider">
                    <div class="template-slide" v-for="(page, pageIndex) in paginatedTemplates" :key="pageIndex">
                <div
                            v-for="template in page"
                    :key="template.id"
                    class="template-card"
                    :class="{ 'multi-filter': template.filters.length > 1 }"
                    @click="selectTemplate(template)"
                >
                    <div class="template-icon" :style="{ background: template.color }">
                        <i class="material-icons">{{ template.icon }}</i>
                        <span v-if="template.filters.length > 1" class="filter-count">{{ template.filters.length }}</span>
                    </div>
                    <div class="template-info">
                        <h4 class="template-name">{{ template.name }}</h4>
                        <p class="template-desc">{{ template.description }}</p>
                    </div>
                        </div>
                    </div>
                </div>
                <div class="slider-controls">
                    <a-button class="control-btn prev" @click="slideTemplatesPrev">
                        <i class="material-icons">chevron_left</i>
                    </a-button>
                    <a-button class="control-btn next" @click="slideTemplatesNext">
                        <i class="material-icons">chevron_right</i>
                    </a-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import CoPilot from "@/utils/CoPilot";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import S2Textarea from "@/components/S2Textarea.vue";
import ChatWatcherV2 from "@/ChatWatcherV2";

export default {
    name: "FilterSuggestionsModal",
    components: { SupsisButton, S2Textarea },
    data() {
        return {
            searchQuery: "",
            aiPrompt: "",
            isGenerating: false,
            aiFilterResult: null,
            showTechnical: false,
            progressMessages: [],
            currentProgress: null,
            selectedGroup: "all",
            currentSlide: 0,
            slideInterval: null,
            currentTemplateSlide: 0,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels || [],
            departments: (state) => state.subject?.subjects?.data || [],
            users: (state) => state.user.users.data || [],
            currentUser: (state) => state.auth.user,
            contactProperties: (state) => state.contactProperty.data || [],
            debugMode: (state) => state.app?.debugMode || false,
        }),
        filterGroups() {
            return [
                { id: "all", name: "Tümü", icon: "apps" },
                { id: "basic", name: "Temel", icon: "tune" },
                { id: "channels", name: "Kanallar", icon: "radio_button_checked" },
                { id: "departments", name: "Departmanlar", icon: "business" },
                { id: "users", name: "Katılımcılar", icon: "person" },
                { id: "compound", name: "Birleşik", icon: "layers" },
                { id: "time", name: "Zaman", icon: "schedule" },
                { id: "status", name: "Durum", icon: "chat_bubble" },
            ];
        },
        filterTemplates() {
            const templates = [];

            // TEMEL FİLTRELER
            const basicTemplates = [
                {
                    id: "unread-messages",
                    name: "Okunmamış Mesajlar",
                    description: "Henüz okunmamış mesajları içeren konuşmalar",
                    icon: "mark_email_unread",
                    color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    group: "basic",
                    filters: [{ field: "unread", value: true }]
                },
                {
                    id: "open-conversations",
                    name: "Açık Konuşmalar",
                    description: "Devam eden tüm açık konuşmalar",
                    icon: "chat_bubble",
                    color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    group: "basic",
                    filters: [{ field: "lastConversationStatus", value: "active" }]
                },
                {
                    id: "vip-customers",
                    name: "VIP Müşteriler",
                    description: "VIP olarak işaretlenmiş müşteriler",
                    icon: "star",
                    color: "linear-gradient(135deg, #ffd89b 0%, #19547b 100%)",
                    group: "basic",
                    filters: [{ field: "vipCustomer", value: "true" }]
                },
                {
                    id: "unassigned",
                    name: "Atanmamış Konuşmalar",
                    description: "Henüz kimseye atanmamış konuşmalar",
                    icon: "person_off",
                    color: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                    group: "basic",
                    filters: [{ field: "owner", value: [{ type: "unowned" }] }]
                },
                {
                    id: "my-conversations",
                    name: "Bana Atanan",
                    description: "Size atanmış tüm konuşmalar",
                    icon: "assignment_ind",
                    color: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
                    group: "basic",
                    filters: [{ field: "owner", value: [{ type: "agent", value: this.currentUser?._id || "" }] }]
                },
                {
                    id: "chatbot-conversations",
                    name: "Chatbot Konuşmaları",
                    description: "Chatbot tarafından işlenen konuşmalar",
                    icon: "smart_toy",
                    color: "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
                    group: "status",
                    filters: [{ field: "lastConversationChatbot", value: "true" }]
                },
                {
                    id: "blocked-contacts",
                    name: "Engellenmiş Kişiler",
                    description: "Engellenen kişilerle yapılan konuşmalar",
                    icon: "block",
                    color: "linear-gradient(135deg, #ff6a88 0%, #ff9a44 100%)",
                    group: "status",
                    filters: [{ field: "blocked", value: "true" }]
                }
            ];
            templates.push(...basicTemplates);

            // ZAMAN FİLTRELERİ
            const timeTemplates = [
                {
                    id: "recent-1-day",
                    name: "Son 1 Gün",
                    description: "Son 24 saat içinde aktivite olan konuşmalar",
                    icon: "schedule",
                    color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    group: "time",
                    filters: [{ field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(1) }]
                },
                {
                    id: "recent-3-days",
                    name: "Son 3 Gün",
                    description: "Son 3 gün içinde aktivite olan konuşmalar",
                    icon: "schedule",
                    color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    group: "time",
                    filters: [{ field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(3) }]
                },
                {
                    id: "recent-7-days",
                    name: "Son 7 Gün",
                    description: "Son 7 gün içinde aktivite olan konuşmalar",
                    icon: "schedule",
                    color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    group: "time",
                    filters: [{ field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(7) }]
                },
                {
                    id: "recent-30-days",
                    name: "Son 30 Gün",
                    description: "Son 30 gün içinde aktivite olan konuşmalar",
                    icon: "schedule",
                    color: "linear-gradient(135deg, #ffd89b 0%, #19547b 100%)",
                    group: "time",
                    filters: [{ field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(30) }]
                }
            ];
            templates.push(...timeTemplates);

            // KANAL FİLTRELERİ
            if (this.channels && this.channels.length > 0) {
                const channelColors = [
                    "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                    "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                    "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)",
                    "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
                    "linear-gradient(135deg, #f09433 0%, #e6683c 100%)"
                ];

                const channelIcons = {
                    whatsapp: "whatsapp",
                    instagram: "camera_alt",
                    facebook: "facebook",
                    webchat: "chat",
                    telegram: "send",
                };

                this.channels.forEach((channel, index) => {
                    // Temel kanal filtresi
                    templates.push({
                        id: `channel-${channel._id}`,
                        name: `${channel.name} Kanalı`,
                        description: `${channel.name} kanalından gelen konuşmalar`,
                        icon: channelIcons[channel.type] || "radio_button_checked",
                        color: channelColors[index % channelColors.length],
                        group: "channels",
                        filters: [{ field: "channel", value: [channel._id] }]
                    });

                    // Kanal + Cevapsızlar
                    templates.push({
                        id: `channel-unanswered-${channel._id}`,
                        name: `${channel.name} Cevapsızlar`,
                        description: `${channel.name} kanalındaki cevaplanmamış konuşmalar`,
                        icon: "mark_email_unread",
                        color: channelColors[index % channelColors.length],
                        group: "compound",
                        filters: [
                            { field: "channel", value: [channel._id] },
                            { field: "answerStatus", value: "unanswered" }
                        ]
                    });

                    // Kanal + Atanmamış
                    templates.push({
                        id: `channel-unassigned-${channel._id}`,
                        name: `${channel.name} Atanmamış`,
                        description: `${channel.name} kanalındaki atanmamış konuşmalar`,
                        icon: "person_off",
                        color: channelColors[index % channelColors.length],
                        group: "compound",
                        filters: [
                            { field: "channel", value: [channel._id] },
                            { field: "owner", value: [{ type: "unowned" }] }
                        ]
                    });

                    // Kanal + VIP
                    templates.push({
                        id: `channel-vip-${channel._id}`,
                        name: `${channel.name} VIP Müşteriler`,
                        description: `${channel.name} kanalındaki VIP müşteriler`,
                        icon: "star",
                        color: channelColors[index % channelColors.length],
                        group: "compound",
                        filters: [
                            { field: "channel", value: [channel._id] },
                            { field: "vipCustomer", value: "true" }
                        ]
                    });

                    // Kanal + Son 7 Gün
                    templates.push({
                        id: `channel-recent-${channel._id}`,
                        name: `${channel.name} Son 7 Gün`,
                        description: `${channel.name} kanalında son 7 gündeki konuşmalar`,
                        icon: "schedule",
                        color: channelColors[index % channelColors.length],
                        group: "compound",
                        filters: [
                            { field: "channel", value: [channel._id] },
                            { field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(7) }
                        ]
                    });
                });
            }

            // DEPARTMAN FİLTRELERİ
            if (this.departments && this.departments.length > 0) {
                const deptColors = [
                    "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)",
                    "linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)",
                    "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
                    "linear-gradient(135deg, #fa8bff 0%, #2bd2ff 100%)",
                    "linear-gradient(135deg, #52fa5a 0%, #4dfcff 100%)"
                ];

                this.departments.forEach((department, index) => {
                    const departmentName = this.getDepartmentName(department);

                    // Temel departman filtresi
                    templates.push({
                        id: `department-${department._id}`,
                        name: `${departmentName} Departmanı`,
                        description: `${departmentName} departmanına ait konuşmalar`,
                        icon: "business",
                        color: deptColors[index % deptColors.length],
                        group: "departments",
                        filters: [{ field: "relatedDepartments", value: [department._id] }]
                    });

                    // Departman + Cevapsızlar
                    templates.push({
                        id: `dept-unanswered-${department._id}`,
                        name: `${departmentName} Cevapsızlar`,
                        description: `${departmentName} departmanındaki cevaplanmamış konuşmalar`,
                        icon: "mark_email_unread",
                        color: deptColors[index % deptColors.length],
                        group: "compound",
                        filters: [
                            { field: "relatedDepartments", value: [department._id] },
                            { field: "answerStatus", value: "unanswered" }
                        ]
                    });

                    // Departman + Atanmamış
                    templates.push({
                        id: `dept-unassigned-${department._id}`,
                        name: `${departmentName} Atanmamış`,
                        description: `${departmentName} departmanındaki atanmamış konuşmalar`,
                        icon: "person_off",
                        color: deptColors[index % deptColors.length],
                        group: "compound",
                        filters: [
                            { field: "relatedDepartments", value: [department._id] },
                            { field: "owner", value: [{ type: "unowned" }] }
                        ]
                    });
                });
            }

            // KULLANICI FİLTRELERİ
            if (this.users && this.users.length > 0) {
                const userColors = [
                    "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                ];

                this.users.slice(0, 8).forEach((user, index) => {
                    // Kullanıcının sahip olduğu konuşmalar
                    templates.push({
                        id: `user-owned-${user._id}`,
                        name: `${user.name} Sahipliği`,
                        description: `${user.name} kullanıcısının sahibi olduğu konuşmalar`,
                        icon: "assignment_ind",
                        color: userColors[index % userColors.length],
                        group: "users",
                        filters: [{ field: "owner", value: [{ type: "agent", value: user._id }] }]
                    });

                    // Kullanıcı + Son konuşma
                    templates.push({
                        id: `user-last-conv-${user._id}`,
                        name: `${user.name} Son Konuşma`,
                        description: `${user.name} ile son konuşması olan kişiler`,
                        icon: "chat",
                        color: userColors[index % userColors.length],
                        group: "compound",
                        filters: [{ field: "lastConversationUsers", value: [{ type: "agent", value: user._id }] }]
                    });
                });
            }

            // PLATFORM FİLTRELERİ
            const platforms = [
                { value: "whatsapp", name: "WhatsApp", icon: "whatsapp", color: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)" },
                { value: "instagram", name: "Instagram", icon: "camera_alt", color: "linear-gradient(135deg, #f09433 0%, #e6683c 50%, #dc2743 75%, #cc2366 100%)" },
                { value: "web", name: "Web", icon: "language", color: "linear-gradient(135deg, #4267B2 0%, #385898 100%)" },
                { value: "telegram", name: "Telegram", icon: "send", color: "linear-gradient(135deg, #0088cc 0%, #005580 100%)" }
            ];

            platforms.forEach(platform => {
                templates.push({
                    id: `platform-${platform.value}`,
                    name: `${platform.name} Platformu`,
                    description: `${platform.name} platformundan gelen konuşmalar`,
                    icon: platform.icon,
                    color: platform.color,
                    group: "basic",
                    filters: [{ field: "platform", value: [platform.value] }]
                });
            });

            // COMPOUND FİLTRELER (Çoklu kombinasyonlar)
            const compoundTemplates = [
                // VIP kombinasyonları
                {
                    id: "unread-vip",
                    name: "Okunmamış VIP",
                    description: "Okunmamış VIP müşteri mesajları",
                    icon: "priority_high",
                    color: "linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)",
                    group: "compound",
                    filters: [
                        { field: "unread", value: true },
                        { field: "vipCustomer", value: "true" }
                    ]
                },
                {
                    id: "active-vip",
                    name: "Aktif VIP Konuşmalar",
                    description: "Aktif durumdaki VIP müşteri konuşmaları",
                    icon: "star_rate",
                    color: "linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)",
                    group: "compound",
                    filters: [
                        { field: "lastConversationStatus", value: "active" },
                        { field: "vipCustomer", value: "true" }
                    ]
                },
                {
                    id: "vip-recent-7days",
                    name: "VIP Son 7 Gün",
                    description: "Son 7 gün içinde aktivite olan VIP müşteriler",
                    icon: "star",
                    color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    group: "compound",
                    filters: [
                        { field: "vipCustomer", value: "true" },
                        { field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(7) }
                    ]
                },

                // Atanmamış kombinasyonları
                {
                    id: "unassigned-recent",
                    name: "Atanmamış Son 3 Gün",
                    description: "Son 3 gün içinde atanmamış konuşmalar",
                    icon: "new_releases",
                    color: "linear-gradient(135deg, #ff9a56 0%, #ff6b95 100%)",
                    group: "compound",
                    filters: [
                        { field: "owner", value: [{ type: "unowned" }] },
                        { field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(3) }
                    ]
                },
                {
                    id: "unassigned-unread",
                    name: "Atanmamış Okunmamış",
                    description: "Atanmamış ve okunmamış konuşmalar",
                    icon: "person_off",
                    color: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                    group: "compound",
                    filters: [
                        { field: "owner", value: [{ type: "unowned" }] },
                        { field: "unread", value: true }
                    ]
                },
                {
                    id: "unassigned-active",
                    name: "Atanmamış Aktif",
                    description: "Atanmamış ve aktif durumdaki konuşmalar",
                    icon: "chat_bubble_outline",
                    color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    group: "compound",
                    filters: [
                        { field: "owner", value: [{ type: "unowned" }] },
                        { field: "lastConversationStatus", value: "active" }
                    ]
                },

                // Okunmamış kombinasyonları
                {
                    id: "unread-recent-1day",
                    name: "Okunmamış Son 1 Gün",
                    description: "Son 24 saat içinde okunmamış mesajlar",
                    icon: "mark_email_unread",
                    color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    group: "compound",
                    filters: [
                        { field: "unread", value: true },
                        { field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(1) }
                    ]
                },
                {
                    id: "unread-active",
                    name: "Okunmamış Aktif",
                    description: "Okunmamış ve aktif durumdaki konuşmalar",
                    icon: "notifications_active",
                    color: "linear-gradient(135deg, #ff6a88 0%, #ff9a44 100%)",
                    group: "compound",
                    filters: [
                        { field: "unread", value: true },
                        { field: "lastConversationStatus", value: "active" }
                    ]
                },

                // Chatbot kombinasyonları
                {
                    id: "chatbot-unread",
                    name: "Chatbot Okunmamış",
                    description: "Chatbot konuşmalarında okunmamış mesajlar",
                    icon: "smart_toy",
                    color: "linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)",
                    group: "compound",
                    filters: [
                        { field: "lastConversationChatbot", value: "true" },
                        { field: "unread", value: true }
                    ]
                },
                {
                    id: "chatbot-active",
                    name: "Chatbot Aktif",
                    description: "Chatbot ile aktif konuşmalar",
                    icon: "android",
                    color: "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
                    group: "compound",
                    filters: [
                        { field: "lastConversationChatbot", value: "true" },
                        { field: "lastConversationStatus", value: "active" }
                    ]
                },

                // Platform kombinasyonları
                {
                    id: "whatsapp-unread",
                    name: "WhatsApp Okunmamış",
                    description: "WhatsApp'tan okunmamış mesajlar",
                    icon: "whatsapp",
                    color: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
                    group: "compound",
                    filters: [
                        { field: "platform", value: ["whatsapp"] },
                        { field: "unread", value: true }
                    ]
                },
                {
                    id: "instagram-active",
                    name: "Instagram Aktif",
                    description: "Instagram'dan aktif konuşmalar",
                    icon: "camera_alt",
                    color: "linear-gradient(135deg, #f09433 0%, #e6683c 100%)",
                    group: "compound",
                    filters: [
                        { field: "platform", value: ["instagram"] },
                        { field: "lastConversationStatus", value: "active" }
                    ]
                },

                // Cevap durumu kombinasyonları
                {
                    id: "unanswered-recent",
                    name: "Cevaplanmamış Son 3 Gün",
                    description: "Son 3 gün içinde cevaplanmamış konuşmalar",
                    icon: "question_answer",
                    color: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                    group: "compound",
                    filters: [
                        { field: "answerStatus", value: "unanswered" },
                        { field: "lastActionTime", operator: "greater_than_or_equal", value: this.getDateDaysAgo(3) }
                    ]
                },
                {
                    id: "unanswered-vip",
                    name: "Cevaplanmamış VIP",
                    description: "Cevaplanmamış VIP müşteri konuşmaları",
                    icon: "help_outline",
                    color: "linear-gradient(135deg, #ffd89b 0%, #19547b 100%)",
                    group: "compound",
                    filters: [
                        { field: "answerStatus", value: "unanswered" },
                        { field: "vipCustomer", value: "true" }
                    ]
                }
            ];
            templates.push(...compoundTemplates);


            return templates;
        },
        filteredTemplates() {
            let templates = this.filterTemplates;

            // Grup filtresi uygula
            if (this.selectedGroup !== "all") {
                templates = templates.filter(template => template.group === this.selectedGroup);
            }

            // Arama filtresi uygula
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                templates = templates.filter(template =>
                    template.name.toLowerCase().includes(query) ||
                    template.description.toLowerCase().includes(query)
                );
            }

            return templates;
        },
        examplePrompts() {
            return [
                "Son 7 gün içinde WhatsApp kanalından gelen VIP müşteriler",
                "Bugün atanmamış ve okunmamış konuşmalar",
                "Instagram kanalından son 3 gün içinde gelen aktif konuşmalar",
                "Chatbot tarafından işlenen ama henüz cevaplanmamış mesajlar",
                "Engellenmiş kişilerle yapılan son konuşmalar",
                "Departmanıma atanmış okunmamış VIP müşteri mesajları",
                "Son 30 gün içinde Telegram kanalından gelen konuşmalar",
                "Bana atanmış ama henüz cevaplamadığım mesajlar",
                "Web kanalından gelen ve 24 saatten uzun süredir bekleyen konuşmalar",
                "Son hafta içinde aktivite olan ama kapatılmamış konuşmalar"
            ];
        },
        paginatedTemplates() {
            const itemsPerPage = 8;
            const templates = this.filteredTemplates;
            const pages = [];
            
            for (let i = 0; i < templates.length; i += itemsPerPage) {
                pages.push(templates.slice(i, i + itemsPerPage));
            }
            
            return pages;
        },
    },
    methods: {
        getDateDaysAgo(days) {
            const date = new Date();
            date.setDate(date.getDate() - days);
            return date.toISOString();
        },
        selectGroup(groupId) {
            this.selectedGroup = groupId;
        },
        getGroupTemplateCount(groupId) {
            if (groupId === "all") return this.filterTemplates.length;
            return this.filterTemplates.filter(template => template.group === groupId).length;
        },
        getDepartmentName(department) {
            // Mevcut dilde departman adını göster, yoksa varsayılan dili kullan
            if (!department || !department.languageContent) return "İsimsiz Departman";

            // Yeni yapı (object olarak)
            if (typeof department.languageContent === "object" && !Array.isArray(department.languageContent)) {
                const currentLang = this.$i18n.locale;
                if (department.languageContent[currentLang] && department.languageContent[currentLang].name) {
                    return department.languageContent[currentLang].name;
                }

                // Mevcut dilde ad yoksa ilk dildeki adı göster
                const firstLang = Object.keys(department.languageContent)[0];
                return department.languageContent[firstLang]?.name || "İsimsiz Departman";
            }

            // Eski yapı (array olarak)
            if (Array.isArray(department.languageContent)) {
                const langItem = department.languageContent.find((item) => item.languageCode === this.$i18n.locale);
                if (langItem && langItem.name) {
                    return langItem.name;
                }

                // Mevcut dilde ad yoksa ilk dildeki adı göster
                return department.languageContent[0]?.name || department.name || "İsimsiz Departman";
            }

            // Hiçbir dilde tanımlanmamışsa eski name alanını kullan
            return department.name || "İsimsiz Departman";
        },
        selectTemplate(template) {
            const filters = JSON.parse(JSON.stringify(template.filters));

            // Backend'e gönderilmeden önce id'leri temizle
            const cleanedFilters = filters.map(filter => {
                const { id, ...cleanFilter } = filter;
                return cleanFilter;
            });

            // Template seçildiğinde yeni filtre oluştur
            const filterData = {
                filters: cleanedFilters,
                name: template.name,
                description: template.description,
                shouldCreateFilter: true
            };

            this.$emit("apply-filters", filterData);
        },
        async generateAiFilter() {
            if (!this.aiPrompt || this.isGenerating) return;

            this.isGenerating = true;
            this.aiFilterResult = null;
            this.progressMessages = [];
            this.currentProgress = null;

            // Progress listener
            const progressListener = (message) => {
                if (message.cmd === "copilot_progress" && message.data.progress) {
                    this.currentProgress = message.data.progress;
                    this.progressMessages.push(message.data.progress);
                }
            };

            // Socket'e progress listener ekle
            const { socketMessageStream } = require("@/utils/socketStream");
            const subscription = socketMessageStream.subscribe(progressListener);

            try {
                const copilot = CoPilot.getInstance();

                const channels = this.channels.map(ch => ({
                    id: ch._id,
                    name: ch.name,
                    type: ch.type
                }));

                const departments = this.departments.map(dept => ({
                    id: dept._id,
                    languageContent: dept.languageContent,
                }));

                const users = this.users.map(user => ({
                    id: user._id,
                    name: user.name,
                    email: user.email,
                    isOnline: user.isOnline,
                }));

                const contactProperties = this.contactProperties.map(prop => ({
                    id: prop._id,
                    name: prop.name,
                    type: prop.type,
                    key: prop.key
                }));

                const ownedChannels = ChatWatcherV2.getOwnedChannels(this.currentUser);
                const availableChannels = ChatWatcherV2.listAvailableChannels(this.currentUser);

                const result = await copilot.suggestFilters({
                    customerPrompt: this.aiPrompt,
                    channels,
                    departments,
                    users,
                    contactProperties,
                    ownedChannels,
                    availableChannels,
                    authUser: {
                        id: this.currentUser._id,
                        name: this.currentUser.name,
                        email: this.currentUser.email,
                        lang: this.currentUser.lang || this.$i18n.locale || 'tr',
                        subjects: this.currentUser.subjects || [],
                        isOnline: this.currentUser.isOnline || false,
                    }
                });

                if (result && !result.error) {
                    this.aiFilterResult = result;
                } else {
                    this.$notification.error({
                        message: "Hata",
                        description: result.message || "Filtre oluşturulurken bir hata oluştu.",
                    });
                }
            } catch (error) {
                this.$notification.error({
                    message: "Hata",
                    description: "Filtre oluşturulurken bir hata oluştu.",
                });
            } finally {
                this.isGenerating = false;
                this.currentProgress = null;
                subscription.unsubscribe();
            }
        },
        createFilterFromAi() {
            if (!this.aiFilterResult || !this.aiFilterResult.filters) return;
            const filters = JSON.parse(JSON.stringify(this.aiFilterResult.filters));

            // Backend'e gönderilmeden önce id'leri temizle
            const cleanedFilters = filters.map(filter => {
                const { id, ...cleanFilter } = filter;
                return cleanFilter;
            });

            // AI'dan gelen filtre bilgilerini emit et
            const filterData = {
                filters: cleanedFilters,
                name: this.aiFilterResult.title || 'Özel Filtre',
                description: this.aiFilterResult.description,
                shouldCreateFilter: true // Yeni filtre oluşturulması gerektiğini belirt
            };

            this.$emit("apply-filters", filterData);
        },
        getFilterFieldLabel(field) {
            const labels = {
                'channel': 'Kanal',
                'relatedDepartments': 'Departman',
                'owner': 'Sahiplik',
                'lastConversationUsers': 'Sohbet Katılımcıları',
                'platform': 'Platform',
                'tags': 'Etiketler',
                'lastConversationStatus': 'Konuşma Durumu',
                'answerStatus': 'Cevap Durumu',
                'vipCustomer': 'VIP Müşteri',
                'unread': 'Okunmamış',
                'blocked': 'Engellenmiş',
                'lastConversationChatbot': 'Chatbot Konuşması',
                'lastConversationIsMissed': 'Kaçan Sohbet',
                'createdAt': 'Oluşturulma Tarihi',
                'lastActionTime': 'Son Aktivite',
                'metaReferral': 'Meta Yönlendirme',
                'lastTriggerState': 'Son Tetikleyici',
                'lastBroadcastInfo': 'Toplu Mesaj Bilgisi',
                'zohoInfo': 'Zoho Bilgisi',
            };

            if (field.startsWith('contactProperties.')) {
                const propertyId = field.replace('contactProperties.', '');
                const property = this.contactProperties.find(p => p._id === propertyId);
                return property ? property.name : 'Kişi Özelliği';
            }

            return labels[field] || field;
        },
        getOperatorLabel(operator) {
            const labels = {
                'equals': 'Eşittir',
                'contains': 'İçerir',
                'greater_than': 'Büyüktür',
                'less_than': 'Küçüktür',
                'greater_than_or_equal': 'Büyük/Eşit',
                'less_than_or_equal': 'Küçük/Eşit',
                'between': 'Arasında',
                'exists': 'Var',
            };
            return labels[operator] || operator;
        },
        getFilterIcon(field) {
            const icons = {
                'channel': 'radio_button_checked',
                'relatedDepartments': 'business',
                'owner': 'person',
                'lastConversationUsers': 'group',
                'platform': 'devices',
                'tags': 'label',
                'lastConversationStatus': 'chat_bubble',
                'answerStatus': 'question_answer',
                'vipCustomer': 'star',
                'unread': 'mark_email_unread',
                'blocked': 'block',
                'lastConversationChatbot': 'smart_toy',
                'lastConversationIsMissed': 'sentiment_dissatisfied',
                'createdAt': 'event',
                'lastActionTime': 'schedule',
                'metaReferral': 'ads_click',
                'lastTriggerState': 'bolt',
                'lastBroadcastInfo': 'campaign',
                'zohoInfo': 'integration_instructions',
            };

            if (field.startsWith('contactProperties.')) {
                return 'text_fields';
            }

            return icons[field] || 'filter_alt';
        },
        formatFilterValueDisplay(filter) {
            const { field, value } = filter;

            // Channel - kanal adlarını göster
            if (field === 'channel') {
                const channelIds = Array.isArray(value) ? value : [value];
                const names = this.channels
                    .filter(ch => channelIds.includes(ch._id))
                    .map(ch => ch.name || 'İsimsiz Kanal');
                if (names.length === 0) return 'Seçili kanallar';
                if (names.length <= 2) return names.join(', ');
                return names.slice(0, 2).join(', ') + ` (+${names.length - 2} more)`;
            }

            // Department - departman adlarını göster
            if (field === 'relatedDepartments') {
                const departmentIds = Array.isArray(value) ? value : [value];
                const names = this.departments
                    .filter(dept => departmentIds.includes(dept._id))
                    .map(dept => this.getDepartmentName(dept));
                if (names.length === 0) return 'Seçili departmanlar';
                if (names.length <= 2) return names.join(', ');
                return names.slice(0, 2).join(', ') + ` (+${names.length - 2} more)`;
            }

            // Owner - kullanıcı adlarını göster
            if (field === 'owner' && Array.isArray(value)) {
                const parts = [];
                const agents = value.filter(v => v.type === 'agent');
                const hasUnowned = value.some(v => v.type === 'unowned');

                if (agents.length > 0) {
                    const userIds = agents.map(v => v.value);
                    const names = this.users
                        .filter(u => userIds.includes(u._id))
                        .map(u => u.name);
                    if (names.length > 0) {
                        if (names.length <= 2) {
                            parts.push(names.join(', '));
                        } else {
                            parts.push(names.slice(0, 2).join(', ') + ` (+${names.length - 2} more)`);
                        }
                    }
                }

                if (hasUnowned) parts.push('Sahipsiz');

                return parts.length > 0 ? parts.join(' ve ') : 'Seçili sahipler';
            }

            // LastConversationUsers
            if (field === 'lastConversationUsers' && Array.isArray(value)) {
                const hasNone = value.some(v => v.type === 'none');
                if (hasNone) return 'Hiç temsilci yok';

                const agents = value.filter(v => v.type === 'agent');
                if (agents.length > 0) {
                    const userIds = agents.map(v => v.value);
                    const names = this.users
                        .filter(u => userIds.includes(u._id))
                        .map(u => u.name);
                    if (names.length === 0) return 'Seçili Katılımcılar';
                    if (names.length <= 2) return names.join(', ');
                    return names.slice(0, 2).join(', ') + ` (+${names.length - 2} more)`;
                }
            }

            // Platform
            if (field === 'platform') {
                const platformValues = Array.isArray(value) ? value : [value];
                const platformNames = {
                    'supsis': 'Supsis',
                    'email': 'Email',
                    'whatsapp': 'WhatsApp',
                    'web': 'Web',
                    'instagram': 'Instagram',
                    'telegram': 'Telegram',
                    'google': 'Google',
                    'messenger': 'Messenger',
                    'calendar': 'Calendar',
                    'meta-leads': 'Meta Leads',
                    'trendyol': 'Trendyol',
                    'linkedin': 'LinkedIn',
                };
                const displayNames = platformValues.map(v => platformNames[v] || v);
                if (displayNames.length <= 2) {
                    return displayNames.join(', ');
                }
                return displayNames.slice(0, 2).join(', ') + ` (+${displayNames.length - 2} more)`;
            }

            // Status values
            if (field === 'lastConversationStatus') {
                const statuses = {
                    'active': 'Aktif',
                    'lost': 'Lost',
                    'new': 'Yeni',
                    'chatbot': 'Chatbot',
                    'destroyed': 'Kapalı'
                };
                return statuses[value] || value;
            }

            if (field === 'answerStatus') {
                const statuses = {
                    'answered': 'Cevaplanmış',
                    'unanswered': 'Cevaplanmamış'
                };
                return statuses[value] || value;
            }

            // Boolean values
            if (field === 'vipCustomer') return value === 'true' ? 'Evet' : 'Hayır';
            if (field === 'unread') return value ? 'Evet' : 'Hayır';
            if (field === 'blocked') return value === 'true' ? 'Evet' : 'Hayır';
            if (field === 'lastConversationChatbot') return value === 'true' ? 'Evet' : 'Hayır';
            if (field === 'lastConversationIsMissed') return value === 'true' ? 'Evet' : 'Hayır';

            // Date
            if (field === 'createdAt' || field === 'lastActionTime') {
                const relativePresets = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'last7Days', 'last30Days', 'thisMonth', 'lastMonth'];
                
                if (typeof value === 'string' && relativePresets.includes(value)) {
                    const presetLabels = {
                        'today': 'Bugün',
                        'yesterday': 'Dün',
                        'thisWeek': 'Bu Hafta',
                        'lastWeek': 'Geçen Hafta',
                        'last7Days': 'Son 7 Gün',
                        'last30Days': 'Son 30 Gün',
                        'thisMonth': 'Bu Ay',
                        'lastMonth': 'Geçen Ay'
                    };
                    return presetLabels[value] || value;
                }
                
                if (Array.isArray(value) && value.length === 2) {
                    const startDate = this.formatDate(value[0]);
                    const endDate = this.formatDate(value[1]);
                    return `${startDate} - ${endDate}`;
                }
                
                // Tek değer ise normal formatla
                const daysAgo = this.getDaysAgo(value);
                if (daysAgo) return `Son ${daysAgo} gün`;
                return this.formatDate(value);
            }

            // Tags - etiket adlarını göster
            if (field === 'tags') {
                const tagIds = Array.isArray(value) ? value : [value];
                const tagNames = [];

                // Store'dan etiketleri al
                const tags = this.$store.state.tag?.tags?.data || [];
                tagIds.forEach(tagId => {
                    const tag = tags.find(t => t._id === tagId);
                    if (tag) {
                        tagNames.push(tag.name);
                    }
                });

                if (tagNames.length === 0) return 'Seçili etiketler';
                if (tagNames.length <= 2) return tagNames.join(', ');
                return tagNames.slice(0, 2).join(', ') + ` (+${tagNames.length - 2} more)`;
            }

            // Default
            return this.formatFilterValue(value);
        },
        formatFilterValue(value) {
            if (value === null) return 'Boş';
            if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';
            if (Array.isArray(value)) {
                if (value.length === 0) return 'Boş';
                if (typeof value[0] === 'object') {
                    const displayValues = value.map(v => v.type || v.value || JSON.stringify(v));
                    if (displayValues.length <= 2) return displayValues.join(', ');
                    return displayValues.slice(0, 2).join(', ') + ` (+${displayValues.length - 2} more)`;
                }
                if (value.length <= 2) return value.join(', ');
                return value.slice(0, 2).join(', ') + ` (+${value.length - 2} more)`;
            }
            if (typeof value === 'object') {
                return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
            }
            return String(value);
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
        },
        getDaysAgo(dateString) {
            if (!dateString) return null;
            const date = new Date(dateString);
            const now = new Date();
            const diffTime = Math.abs(now - date);
            const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

            // Tam gün sayısını döndür
            if (diffDays >= 1) return diffDays;
            return null;
        },
        selectExamplePrompt(prompt) {
            this.aiPrompt = prompt;
            // Seçilen prompt'u textarea'ya odakla
            this.$nextTick(() => {
                if (this.$refs.aiTextarea) {
                    this.$refs.aiTextarea.focus();
                }
            });
        },
        slideNext() {
            const slider = this.$refs.slider;
            if (!slider) return;

            // Her seferinde 3 item kaydır
            const itemsPerSlide = 3;
            const maxSlides = Math.ceil(this.examplePrompts.length / itemsPerSlide) - 1;

            this.currentSlide = (this.currentSlide + 1) % (maxSlides + 1);

            // Her slide'ın genişliği container'ın genişliği
            const slideWidth = slider.offsetWidth;
            slider.scrollTo({
                left: this.currentSlide * slideWidth,
                behavior: "smooth",
            });
        },
        slidePrev() {
            const slider = this.$refs.slider;
            if (!slider) return;

            // Her seferinde 3 item kaydır
            const itemsPerSlide = 3;
            const maxSlides = Math.ceil(this.examplePrompts.length / itemsPerSlide) - 1;

            this.currentSlide = this.currentSlide === 0 ? maxSlides : this.currentSlide - 1;

            // Her slide'ın genişliği container'ın genişliği
            const slideWidth = slider.offsetWidth;
            slider.scrollTo({
                left: this.currentSlide * slideWidth,
                behavior: "smooth",
            });
        },
        slideTemplatesNext() {
            const slider = this.$refs.templatesSlider;
            if (!slider) return;

            const maxPages = this.paginatedTemplates.length - 1;
            this.currentTemplateSlide = (this.currentTemplateSlide + 1) % (maxPages + 1);

            const slideWidth = slider.offsetWidth;
            slider.scrollTo({
                left: this.currentTemplateSlide * slideWidth,
                behavior: "smooth",
            });
        },
        slideTemplatesPrev() {
            const slider = this.$refs.templatesSlider;
            if (!slider) return;

            const maxPages = this.paginatedTemplates.length - 1;
            this.currentTemplateSlide = this.currentTemplateSlide === 0 ? maxPages : this.currentTemplateSlide - 1;

            const slideWidth = slider.offsetWidth;
            slider.scrollTo({
                left: this.currentTemplateSlide * slideWidth,
                behavior: "smooth",
            });
        },
    },
};
</script>

<style lang="scss" scoped>
// Renk Paleti: #22272d
$primary: #22272d;
$primary-light: #374151;
$primary-lighter: #4b5563;
$bg-light: #f9fafb;
$bg-white: #ffffff;
$border: #e5e7eb;
$border-dark: #d1d5db;
$text-primary: #111827;
$text-secondary: #6b7280;
$text-muted: #9ca3af;

.filter-suggestions-modal {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: $bg-white;

    // Header
    .modal-header {
        padding: 24px 28px;
        border-bottom: 1px solid $border;

        .header-left {
            display: flex;
            align-items: center;
            gap: 14px;
        }

        .header-icon {
            width: 40px;
            height: 40px;
            background: $primary;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                font-size: 22px;
                color: white;
            }
        }

        .header-title {
            font-size: 18px;
            font-weight: 700;
            color: $text-primary;
            margin: 0 0 4px 0;
        }

        .header-subtitle {
            font-size: 13px;
            color: $text-secondary;
            margin: 0;
        }
    }

    // AI Section
    .ai-section {
        padding: 24px 28px;
        background: $bg-light;
        border-bottom: 1px solid $border;

        .ai-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 14px;
            font-size: 14px;
            font-weight: 600;
            color: $text-primary;

            .material-icons {
                font-size: 18px;
                color: $primary;
            }
        }

        .ai-input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .progress-indicator {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 14px;
            padding: 12px 14px;
            background: white;
            border: 1px solid $border;
            border-radius: 6px;
            font-size: 13px;
            color: $text-secondary;
        }

        // AI Result
        .ai-result-container {
            margin-top: 18px;
            padding: 20px;
            background: white;
            border: 1px solid $border;
            border-radius: 8px;

            .result-header {
                margin-bottom: 18px;
                padding-bottom: 16px;
                border-bottom: 1px solid $border;

                .result-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: $text-primary;
                    margin: 0 0 6px 0;
                }

                .result-description {
                    font-size: 13px;
                    color: $text-secondary;
                    margin: 0;
                    line-height: 1.5;
                }
            }

            .filters-container {
                margin-bottom: 18px;

                .filters-count {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: $text-primary;

                    .material-icons {
                        font-size: 16px;
                        color: $primary;
                    }
                }

                .filters-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                    max-height: 300px;
                    overflow-y: auto;
                    padding-right: 4px;

                    // Custom scrollbar
                    &::-webkit-scrollbar {
                        width: 6px;
                    }

                    &::-webkit-scrollbar-track {
                        background: $bg-light;
                        border-radius: 3px;
                    }

                    &::-webkit-scrollbar-thumb {
                        background: $border-dark;
                        border-radius: 3px;

                        &:hover {
                            background: $primary-lighter;
                        }
                    }

                    .filter-item {
                        display: flex;
                        gap: 10px;
                        padding: 12px;
                        background: $bg-light;
                        border: 1px solid $border;
                        border-radius: 6px;
                        transition: all 0.15s;

                        &:hover {
                            background: white;
                            border-color: $border-dark;
                        }

                        .filter-number {
                            width: 20px;
                            height: 20px;
                            background: $primary;
                            color: white;
                            border-radius: 4px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 11px;
                            font-weight: 700;
                            flex-shrink: 0;
                        }

                        .filter-body {
                            flex: 1;
                            min-width: 0;

                            .filter-label {
                                font-size: 11px;
                                font-weight: 600;
                                color: $text-secondary;
                                text-transform: uppercase;
                                letter-spacing: 0.3px;
                                margin-bottom: 4px;
                            }

                            .filter-value-row {
                                display: flex;
                                align-items: center;
                                gap: 6px;

                                .filter-operator {
                                    font-size: 10px;
                                    font-weight: 600;
                                    color: $primary;
                                    background: #f3f4f6;
                                    padding: 2px 6px;
                                    border-radius: 3px;
                                }

                                .filter-value {
                                    font-size: 13px;
                                    font-weight: 500;
                                    color: $text-primary;
                                    white-space: nowrap;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                }
                            }
                        }
                    }
                }

                // Debug
                .debug-panel {
                    margin-top: 14px;

                    .debug-title {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        margin-bottom: 8px;
                        font-size: 11px;
                        font-weight: 600;
                        color: #dc2626;
                        text-transform: uppercase;

                        .material-icons {
                            font-size: 14px;
                        }
                    }

                    .debug-code {
                        padding: 12px;
                        background: #0f172a;
                        color: #22d3ee;
                        border-radius: 6px;
                        font-size: 11px;
                        font-family: 'Courier New', monospace;
                        max-height: 200px;
                        overflow-y: auto;
                        line-height: 1.5;
                    }
                }
            }

            .result-actions {
                display: flex;
                gap: 10px;
                padding-top: 16px;
                border-top: 1px solid $border;
            }
        }

        // Örnek Promptlar
        .example-prompts {
            margin-top: 18px;
            padding: 20px;
            background: white;
            border: 1px solid $border;
            border-radius: 8px;

            h4 {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                font-size: 14px;
                font-weight: 600;
                color: $text-primary;

                &:before {
                    content: "💡";
                    font-size: 16px;
                }
            }

            .prompt-slider-container {
                position: relative;
                width: 100%;
                padding: 0 30px;

                .prompt-slider {
                    display: flex;
                    overflow-x: hidden;
                    scroll-snap-type: x mandatory;
                    gap: 12px;
                    padding: 8px 0;
                    scroll-behavior: smooth;

                    .prompt-slide {
                        flex: 0 0 calc(33.333% - 8px);
                        scroll-snap-align: start;
                    }
                }

                .slider-controls {
                    position: absolute;
                    width: calc(100% + 60px);
                    top: 50%;
                    left: -30px;
                    transform: translateY(-50%);
                    display: flex;
                    justify-content: space-between;
                    pointer-events: none;

                    .control-btn {
                        pointer-events: auto;
                        background: $bg-white;
                        border: 1px solid $border-dark;
                        box-shadow: 0 2px 6px rgba(34, 39, 45, 0.1);
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        border-radius: 50%;
                        z-index: 2;

                        .material-icons {
                            font-size: 16px;
                            color: $text-secondary;
                        }

                        &:hover {
                            background: $primary;
                            border-color: $primary;
                            box-shadow: 0 4px 8px rgba(34, 39, 45, 0.15);

                            .material-icons {
                                color: white;
                            }
                        }
                    }
                }
            }

            .prompt-card {
                background: $bg-light;
                border: 1px solid $border;
                padding: 14px;
                border-radius: 6px;
                transition: all 0.2s ease;
                height: 100%;
                display: flex;
                flex-direction: column;
                cursor: pointer;
                position: relative;

                .material-icons {
                    font-size: 18px;
                    color: $primary;
                    margin-bottom: 8px;
                    align-self: flex-start;
                }

                p {
                    margin: 0;
                    color: $text-secondary;
                    font-size: 12px;
                    line-height: 1.4;
                    flex: 1;
                }

                &:hover {
                    background: white;
                    border-color: $primary-lighter;
                    box-shadow: 0 2px 8px rgba(34, 39, 45, 0.08);
                    transform: translateY(-1px);

                    .material-icons {
                        color: $primary;
                    }

                    p {
                        color: $text-primary;
                    }
                }

                &:active {
                    transform: scale(0.99);
                }
            }
        }
    }

    // Templates
    .templates-section {
        flex: 1;
        padding: 24px 28px;
        overflow-y: auto;

        .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 18px;

            .section-title {
                font-size: 14px;
                font-weight: 700;
                color: $text-primary;
                margin: 0;
            }

            .search-box {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 0 12px;
                height: 34px;
                background: white;
                border: 1px solid $border;
                border-radius: 6px;

                &:focus-within {
                    border-color: $primary-lighter;
                }

                .material-icons {
                    font-size: 18px;
                    color: $text-muted;
                }

                .search-input {
                    border: none;
                    outline: none;
                    font-size: 13px;
                    width: 200px;
                    background: transparent;

                    &::placeholder {
                        color: $text-muted;
                    }
                }
            }
        }

        // Filter Groups
        .filter-groups {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 1px solid $border;

            .group-badge {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 8px 12px;
                background: white;
                border: 1px solid $border;
                border-radius: 20px;
                cursor: pointer;
                transition: all 0.2s;
                font-size: 13px;
                font-weight: 500;
                color: $text-secondary;

                &:hover {
                    border-color: $primary-lighter;
                    background: $bg-light;
                }

                &.active {
                    background: $primary;
                    border-color: $primary;
                    color: white;

                    .group-count {
                        background: rgba(255, 255, 255, 0.2);
                        color: white;
                    }
                }

                .material-icons {
                    font-size: 16px;
                }

                .group-count {
                    background: $bg-light;
                    color: $text-muted;
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-size: 11px;
                    font-weight: 600;
                    min-width: 18px;
                    text-align: center;
                }
            }
        }

        .templates-slider-container {
            position: relative;
            width: 100%;
            padding: 0 40px;
            margin-top: 10px;
        }

        .templates-grid {
            display: flex;
            overflow-x: hidden;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            gap: 0;

            .template-slide {
                flex: 0 0 100%;
                scroll-snap-align: start;
            display: grid;
            grid-template-columns: repeat(4, 1fr);
                grid-template-rows: repeat(2, 1fr);
                gap: 8px;
                padding: 4px;
            }

            .template-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 12px 8px;
                background: white;
                border: 1px solid $border;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.2s;
                position: relative;
                height: 100%;

                &:hover {
                    border-color: $primary;
                    box-shadow: 0 2px 8px rgba(34, 39, 45, 0.08);
                }

                &.multi-filter {
                    border-left: 3px solid $primary;

                    &:hover {
                        box-shadow: 0 4px 12px rgba(34, 39, 45, 0.12);
                    }
                }

                .template-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 8px;
                    position: relative;

                    .material-icons {
                        font-size: 18px;
                        color: white;
                    }

                    .filter-count {
                        position: absolute;
                        top: -4px;
                        right: -4px;
                        background: $primary;
                        color: white;
                        border-radius: 50%;
                        width: 14px;
                        height: 14px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 9px;
                        font-weight: 700;
                        border: 2px solid white;
                    }
                }

                .template-info {
                    text-align: center;
                    width: 100%;

                    .template-name {
                        font-size: 11px;
                        font-weight: 600;
                        color: $text-primary;
                        margin: 0 0 3px 0;
                        line-height: 1.2;
                    }

                    .template-desc {
                        font-size: 9px;
                        color: $text-secondary;
                        margin: 0;
                        line-height: 1.3;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }
                }
            }
        }

        .slider-controls {
            position: absolute;
            width: 100%;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            display: flex;
            justify-content: space-between;
            pointer-events: none;
            padding: 0 5px;

            .control-btn {
                pointer-events: auto;
                background: $bg-white;
                border: 1px solid $border-dark;
                box-shadow: 0 2px 8px rgba(34, 39, 45, 0.15);
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;
                border-radius: 50%;
                z-index: 10;

                .material-icons {
                    font-size: 22px;
                    color: $text-secondary;
                }

                &:hover {
                    background: $primary;
                    border-color: $primary;
                    box-shadow: 0 4px 12px rgba(34, 39, 45, 0.2);

                    .material-icons {
                        color: white;
                    }
                }

                &:active {
                    transform: scale(0.95);
                }
            }
        }
    }
}
</style>
